import { useState } from "react";
import { List } from "react-window";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ListTree,
  Upload,
  Sparkles,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  FolderTree,
  Folder,
  FileText,
  Loader2,
} from "lucide-react";

const TYPE_META = {
  module: { icon: FolderTree, child: "section", childLabel: "Add section" },
  section: { icon: Folder, child: "document", childLabel: "Add document" },
  document: { icon: FileText, child: null, childLabel: null },
};

const ACCEPT_RE = /\.(pdf|csv|txt|md|markdown|text)$/i;
const LARGE_FILE_BYTES = 3 * 1024 * 1024; // route big / CSV indexes through the background importer
const isBigIndex = (f) => /\.csv$/i.test(f.name) || f.size > LARGE_FILE_BYTES;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Recursively read a dropped file/folder entry into a flat list of File objects.
function readEntry(entry) {
  return new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) {
      entry.file((f) => resolve([f]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const collected = [];
      const readBatch = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) {
            const nested = await Promise.all(collected.map(readEntry));
            resolve(nested.flat());
          } else {
            collected.push(...entries);
            readBatch();
          }
        }, () => resolve([]));
      };
      readBatch();
    } else {
      resolve([]);
    }
  });
}


// Flatten tree into ordered rows, carrying sibling context for reordering.
function toRows(tree, depth = 0, out = []) {
  const siblings = tree;
  tree.forEach((node, index) => {
    out.push({ node, depth, siblings, index });
    if (node.children && node.children.length) {
      toRows(node.children, depth + 1, out);
    }
  });
  return out;
}

// One index row (module / section / document). Shared by the plain list and the
// virtualized list; `style` is supplied by react-window when virtualized.
function IndexNodeRow({ rowObj, ctx, style }) {
  const { node, depth, siblings, index: sibIndex } = rowObj;
  const { editingId, editValue, setEditingId, setEditValue, rename, move, addChild, remove } = ctx;
  const meta = TYPE_META[node.type];
  const Icon = meta.icon;
  const isEditing = editingId === node.id;
  return (
    <div style={{ ...(style || {}), paddingLeft: depth * 22 }}>
      <div
        className={`group flex items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-secondary/60 ${
          depth > 0 ? "border-l border-border" : ""
        }`}
        data-testid={`tree-node-${node.id}`}
      >
        <Icon size={15} className={node.type === "module" ? "text-primary" : "text-muted-foreground"} />
        {isEditing ? (
          <div className="flex flex-1 items-center gap-1">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && rename(node)}
              autoFocus
              className="h-7 rounded-sm py-0 text-sm"
              data-testid={`edit-input-${node.id}`}
            />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => rename(node)} data-testid={`save-node-${node.id}`}>
              <Check size={14} />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
              <X size={14} />
            </Button>
          </div>
        ) : (
          <>
            <span className={`flex-1 truncate text-sm ${node.type === "module" ? "font-medium" : ""}`}>
              {node.title}
            </span>
            {node.type === "document" && node.status && <StatusBadge status={node.status} />}
            <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(siblings, sibIndex, -1)}>
                <ChevronUp size={14} />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(siblings, sibIndex, 1)}>
                <ChevronDown size={14} />
              </Button>
              {meta.child && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => addChild(node)}
                  title={meta.childLabel}
                  data-testid={`add-child-${node.id}`}
                >
                  <Plus size={14} />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => {
                  setEditValue(node.title);
                  setEditingId(node.id);
                }}
                data-testid={`edit-node-${node.id}`}
              >
                <Pencil size={13} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive"
                onClick={() => remove(node)}
                data-testid={`delete-node-${node.id}`}
              >
                <Trash2 size={13} />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// react-window v2 row adapter.
function VirtualRow({ index, style, rows, ctx }) {
  return <IndexNodeRow rowObj={rows[index]} ctx={ctx} style={style} />;
}

export default function IndexManager({ projectId, tree, reloadTree }) {
  const [prompt, setPrompt] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [upLoading, setUpLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe the publication first");
      return;
    }
    setGenLoading(true);
    try {
      await api.post(`/projects/${projectId}/generate-index`, { prompt });
      await reloadTree();
      toast.success("Blueprint generated");
      setPrompt("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenLoading(false);
    }
  };

  const importLarge = async (file) => {
    setUpLoading(true);
    setProgress({ importing: true, done: 0, total: 0, name: file.name });
    const mode = tree.length === 0 ? "replace" : "append";
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/projects/${projectId}/import-index-async?mode=${mode}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const jid = data.job_id;
      setProgress({ importing: true, done: 0, total: data.total || 0, name: file.name });
      // Poll the background bulk import until done/error.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await sleep(1500);
        const { data: j } = await api.get(`/import-jobs/${jid}`);
        setProgress({ importing: true, done: j.done || 0, total: j.total || 0, name: file.name });
        if (j.status === "done") {
          const c = j.counts || {};
          toast.success(
            `Imported ${(c.documents || 0).toLocaleString()} documents` +
              (c.modules ? ` across ${c.modules.toLocaleString()} modules` : "")
          );
          break;
        }
        if (j.status === "error") {
          toast.error(j.error || "Import failed");
          break;
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setProgress(null);
      setUpLoading(false);
      await reloadTree();
    }
  };

  const processFiles = async (files) => {
    const accepted = files.filter((f) => ACCEPT_RE.test(f.name));
    const rejected = files.length - accepted.length;
    if (!accepted.length) {
      toast.error(rejected ? "No supported files — use PDF, CSV, TXT or MD" : "No files selected");
      return;
    }
    // A single big/CSV index goes through the resilient background bulk importer (handles 10k+ rows).
    if (accepted.length === 1 && isBigIndex(accepted[0])) {
      await importLarge(accepted[0]);
      if (rejected) toast.info(`Skipped ${rejected} unsupported file${rejected === 1 ? "" : "s"}`);
      return;
    }
    setUpLoading(true);
    let modules = 0;
    let docs = 0;
    const failed = [];
    for (let i = 0; i < accepted.length; i++) {
      setProgress({ done: i, total: accepted.length, name: accepted[i].name });
      try {
        const fd = new FormData();
        fd.append("file", accepted[i]);
        const { data } = await api.post(`/projects/${projectId}/extract-index`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        modules += data.added?.modules || 0;
        docs += data.added?.documents || 0;
        if (accepted.length <= 10) await reloadTree();
      } catch {
        failed.push(accepted[i].name);
      }
    }
    setProgress(null);
    await reloadTree();
    const ok = accepted.length - failed.length;
    if (ok > 0) {
      if (docs > 0 || modules > 0) {
        toast.success(
          `Imported ${docs} document${docs === 1 ? "" : "s"}` +
            (modules ? ` (${modules} new module${modules === 1 ? "" : "s"})` : "") +
            ` from ${ok} file${ok === 1 ? "" : "s"}`
        );
      } else {
        toast.success(`Processed ${ok} file${ok === 1 ? "" : "s"} — duplicates merged, nothing new added`);
      }
    }
    if (failed.length) toast.error(`Couldn't read: ${failed.join(", ")}`);
    if (rejected) toast.info(`Skipped ${rejected} unsupported file${rejected === 1 ? "" : "s"}`);
    setUpLoading(false);
  };

  const upload = async (e) => {
    await processFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    if (upLoading) return;
    const items = e.dataTransfer?.items;
    let files = [];
    if (items && items.length && items[0].webkitGetAsEntry) {
      const entries = Array.from(items)
        .map((it) => it.webkitGetAsEntry && it.webkitGetAsEntry())
        .filter(Boolean);
      const nested = await Promise.all(entries.map(readEntry));
      files = nested.flat();
    } else {
      files = Array.from(e.dataTransfer?.files || []);
    }
    await processFiles(files);
  };

  const addModule = async () => {
    await api.post(`/projects/${projectId}/nodes`, { type: "module", title: "New Module", parent_id: null });
    await reloadTree();
  };

  const addChild = async (parent) => {
    const meta = TYPE_META[parent.type];
    if (!meta.child) return;
    await api.post(`/projects/${projectId}/nodes`, {
      type: meta.child,
      title: meta.child === "section" ? "New Section" : "New Document",
      parent_id: parent.id,
    });
    await reloadTree();
  };

  const rename = async (node) => {
    if (editValue.trim() && editValue !== node.title) {
      await api.put(`/nodes/${node.id}`, { title: editValue.trim() });
      await reloadTree();
    }
    setEditingId(null);
  };

  const remove = async (node) => {
    await api.delete(`/nodes/${node.id}`);
    await reloadTree();
    toast.success("Removed");
  };

  const move = async (siblings, index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[index], reordered[j]] = [reordered[j], reordered[index]];
    const items = reordered.map((n, i) => ({ id: n.id, parent_id: n.parent_id, order: i }));
    await api.post(`/projects/${projectId}/nodes/reorder`, { items });
    await reloadTree();
  };

  const rows = toRows(tree);
  const empty = tree.length === 0;
  const ctx = { editingId, editValue, setEditingId, setEditValue, rename, move, addChild, remove };
  const virtualize = rows.length > 200;
  const listHeight = Math.min(rows.length * 36, 640);

  return (
    <div className="animate-fade-in space-y-8">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-sm border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles size={18} />
            <h3 className="font-serif text-lg font-semibold">Generate from a prompt</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Describe your publication and Espresso will draft the full hierarchy.
          </p>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. A beginner-friendly guide to the human body covering cells, organs and body systems."
            className="mt-4 min-h-[110px] rounded-sm"
            data-testid="index-prompt-input"
          />
          <Button onClick={generate} disabled={genLoading} className="mt-4 rounded-sm" data-testid="generate-index-button">
            {genLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
            Generate blueprint
          </Button>
        </div>

        <div className="rounded-sm border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-primary">
            <Upload size={18} />
            <h3 className="font-serif text-lg font-semibold">Upload PDF, CSV or text indexes</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Drag a whole folder or several files (PDF, CSV, TXT, MD) — Espresso extracts each into structure and merges them onto your blueprint.
          </p>
          <label
            className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-sm border border-dashed py-8 text-center transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!upLoading) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="index-dropzone"
          >
            {upLoading ? (
              <Loader2 size={22} className="animate-spin text-muted-foreground" />
            ) : (
              <Upload size={22} className={dragOver ? "text-primary" : "text-muted-foreground"} />
            )}
            <span className="mt-2 text-sm text-muted-foreground" data-testid="upload-status">
              {upLoading
                ? progress
                  ? progress.importing
                    ? `Importing ${(progress.done || 0).toLocaleString()}${progress.total ? " of " + progress.total.toLocaleString() : ""} — ${progress.name}`
                    : `Processing ${progress.done + 1} of ${progress.total} — ${progress.name}`
                  : "Extracting…"
                : dragOver
                ? "Drop your files or folder to import"
                : "Drag a folder or files here, or click to choose (PDF, CSV, TXT, MD)"}
            </span>
            <input
              type="file"
              multiple
              accept=".pdf,.csv,.txt,.md,.markdown,application/pdf,text/csv,text/plain,text/markdown,application/vnd.ms-excel"
              className="hidden"
              onChange={upload}
              disabled={upLoading}
              data-testid="index-upload-input"
            />
          </label>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <ListTree size={18} className="text-primary" />
            <h3 className="font-serif text-lg font-semibold">Structure blueprint</h3>
          </div>
          <Button variant="outline" size="sm" onClick={addModule} className="rounded-sm" data-testid="add-module-button">
            <Plus size={15} className="mr-1" /> Module
          </Button>
        </div>
        <div className="p-4">
          {empty ? (
            <div className="py-16 text-center text-muted-foreground" data-testid="index-empty">
              No structure yet. Generate from a prompt, upload a PDF, or add a module manually.
            </div>
          ) : (
            <div data-testid="index-tree">
              {rows.length > 200 && (
                <div className="mb-2 text-xs text-muted-foreground" data-testid="index-tree-count">
                  {rows.length.toLocaleString()} items · scroll to browse
                </div>
              )}
              {virtualize ? (
                <List
                  rowComponent={VirtualRow}
                  rowCount={rows.length}
                  rowHeight={36}
                  rowProps={{ rows, ctx }}
                  style={{ height: listHeight }}
                  overscanCount={12}
                />
              ) : (
                <div className="space-y-1">
                  {rows.map((r) => (
                    <IndexNodeRow key={r.node.id} rowObj={r} ctx={ctx} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
